// Song / arrangement state — clip-based timeline.
//
//   pattern  — transport loops the active pattern forever
//   song     — transport plays the timeline; gaps between clips are silence
//
// A clip occupies a contiguous range of bars on the timeline:
//   { id, patternId, startBar, lengthBars }
// Bars are 16 steps long. If a clip's pattern is shorter than its allotted
// span the pattern wraps; if longer it gets truncated. Overlapping clips are
// rejected — addClip / moveClip / resizeClip return false on collision.

const STEPS_PER_BAR = 16;

export function createSong(bus) {
    let mode = 'pattern';
    let clips = []; // [{ id, patternId, startBar, lengthBars }]
    let cursorBar = 0;
    // Audio tracks live in the arrangement alongside pattern clips. Each track
    // gets its own mixer channel; clips reference an audio buffer key in IDB
    // and a startBar where playback should fire.
    let audioTracks = []; // [{ id, name, channelId, clips: [{ id, bufferKey, startBar, durationSec, name }] }]

    const makeId = () => `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;

    function overlaps(a, b) {
        return a.startBar < b.startBar + b.lengthBars && b.startBar < a.startBar + a.lengthBars;
    }
    function overlapsAny(probe, exceptId) {
        return clips.some(c => c.id !== exceptId && overlaps(c, probe));
    }

    function setMode(m) {
        if (m !== 'pattern' && m !== 'song') return;
        if (mode === m) return;
        mode = m;
        cursorBar = 0;
        bus.emit('song:modeChanged', m);
        bus.emit('song:cursorChanged', cursorBar);
    }
    function getMode() { return mode; }

    function addClip({ patternId, startBar, lengthBars = 1 }) {
        const probe = { startBar, lengthBars };
        if (overlapsAny(probe)) return null;
        const clip = { id: makeId(), patternId, startBar, lengthBars };
        clips.push(clip);
        bus.emit('song:clipsChanged', clips);
        return clip;
    }
    function removeClip(id) {
        const before = clips.length;
        clips = clips.filter(c => c.id !== id);
        if (clips.length !== before) bus.emit('song:clipsChanged', clips);
    }
    function moveClip(id, startBar) {
        const clip = clips.find(c => c.id === id);
        if (!clip || clip.startBar === startBar) return false;
        startBar = Math.max(0, startBar);
        if (overlapsAny({ startBar, lengthBars: clip.lengthBars }, id)) return false;
        clip.startBar = startBar;
        bus.emit('song:clipsChanged', clips);
        return true;
    }
    function resizeClip(id, lengthBars) {
        const clip = clips.find(c => c.id === id);
        if (!clip) return false;
        // Allow fractional lengths (e.g. 0.5 for an 8-step pattern); the floor
        // is one 16th-step so the clip can't shrink to zero.
        lengthBars = Math.max(1 / STEPS_PER_BAR, lengthBars);
        if (clip.lengthBars === lengthBars) return false;
        if (overlapsAny({ startBar: clip.startBar, lengthBars }, id)) return false;
        clip.lengthBars = lengthBars;
        bus.emit('song:clipsChanged', clips);
        return true;
    }
    function changeClipPattern(id, patternId) {
        const clip = clips.find(c => c.id === id);
        if (!clip || clip.patternId === patternId) return;
        clip.patternId = patternId;
        bus.emit('song:clipsChanged', clips);
    }
    function getClips() { return clips.slice(); }

    function findClipAt(bar) {
        return clips.find(c => c.startBar <= bar && bar < c.startBar + c.lengthBars) || null;
    }
    // Step-based lookup so fractional clip lengths (e.g. 0.5 bars for an
    // 8-step pattern) are handled correctly during playback.
    function findClipAtStep(step) {
        return clips.find(c => {
            const start = c.startBar * STEPS_PER_BAR;
            const end = start + c.lengthBars * STEPS_PER_BAR;
            return start <= step && step < end;
        }) || null;
    }
    function totalBars() {
        return clips.reduce((m, c) => Math.max(m, c.startBar + c.lengthBars), 0);
    }

    function getCursorBar() { return cursorBar; }
    function setCursorBar(b) {
        if (cursorBar === b) return;
        cursorBar = b;
        bus.emit('song:cursorChanged', cursorBar);
    }

    function clear() {
        if (clips.length === 0 && cursorBar === 0) return;
        clips = []; cursorBar = 0;
        bus.emit('song:clipsChanged', clips);
        bus.emit('song:cursorChanged', cursorBar);
    }
    function replaceAll(newClips) {
        clips = (newClips || []).map(c => ({
            id: c.id || makeId(),
            patternId: c.patternId,
            // startBar stays integer (clips snap to bar grid); lengthBars can
            // be fractional so half-bar (8-step) and 2-bar (32-step) clips
            // round-trip through save/load correctly.
            startBar: c.startBar | 0,
            lengthBars: Math.max(1 / STEPS_PER_BAR, Number(c.lengthBars) || 1),
        }));
        cursorBar = 0;
        bus.emit('song:clipsChanged', clips);
        bus.emit('song:cursorChanged', cursorBar);
    }
    // Drop any clips whose pattern was deleted from the bank.
    function pruneRemoved(patternId) {
        const before = clips.length;
        clips = clips.filter(c => c.patternId !== patternId);
        if (clips.length !== before) bus.emit('song:clipsChanged', clips);
    }

    // ── Audio tracks ──────────────────────────────────────────────────
    const makeTrackId = () => `at_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
    const makeAudioClipId = () => `ac_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;

    function addAudioTrack({ id, name, channelId } = {}) {
        const tid = id || makeTrackId();
        const trackName = name || `Audio ${audioTracks.length + 1}`;
        const track = { id: tid, name: trackName, channelId: channelId || tid, clips: [] };
        audioTracks.push(track);
        bus.emit('audio:tracksChanged', audioTracks);
        return track;
    }
    function removeAudioTrack(id) {
        const before = audioTracks.length;
        audioTracks = audioTracks.filter(t => t.id !== id);
        if (audioTracks.length !== before) bus.emit('audio:tracksChanged', audioTracks);
    }
    function renameAudioTrack(id, name) {
        const t = audioTracks.find(x => x.id === id);
        if (!t || t.name === name) return;
        t.name = name;
        bus.emit('audio:tracksChanged', audioTracks);
    }
    function getAudioTracks() {
        return audioTracks.map(t => ({ ...t, clips: t.clips.slice() }));
    }
    function getAudioTrack(id) {
        return audioTracks.find(t => t.id === id) || null;
    }

    // Audio-clip schema (each field optional with sensible defaults so old
    // saved projects keep loading):
    //   bufferKey      — IDB key for the audio blob
    //   startBar       — timeline position (fractional allowed for left-trim)
    //   durationSec    — original length of the audio buffer (for reference)
    //   offsetSec      — where in the buffer to start playback (left-trim)
    //   playSec        — how long to play from offsetSec (right-trim);
    //                    defaults to durationSec when undefined
    //   fadeInSec      — Tone.Player fadeIn applied each trigger
    //   fadeOutSec     — Tone.Player fadeOut applied each trigger
    function addAudioClip(trackId, opts = {}) {
        const track = audioTracks.find(t => t.id === trackId);
        if (!track) return null;
        const { id, bufferKey, startBar, durationSec, name } = opts;
        const clip = {
            id: id || makeAudioClipId(),
            bufferKey,
            startBar: Math.max(0, Number(startBar) || 0),
            durationSec,
            name,
            offsetSec: Number(opts.offsetSec) || 0,
            playSec: Number.isFinite(opts.playSec) ? opts.playSec : durationSec,
            fadeInSec: Number(opts.fadeInSec) || 0,
            fadeOutSec: Number(opts.fadeOutSec) || 0,
        };
        track.clips.push(clip);
        bus.emit('audio:tracksChanged', audioTracks);
        return clip;
    }
    function removeAudioClip(trackId, clipId) {
        const track = audioTracks.find(t => t.id === trackId);
        if (!track) return null;
        const removed = track.clips.find(c => c.id === clipId) || null;
        track.clips = track.clips.filter(c => c.id !== clipId);
        if (removed) bus.emit('audio:tracksChanged', audioTracks);
        return removed;
    }
    function moveAudioClip(trackId, clipId, startBar) {
        const track = audioTracks.find(t => t.id === trackId);
        if (!track) return false;
        const c = track.clips.find(c => c.id === clipId);
        if (!c) return false;
        const next = Math.max(0, Number(startBar) || 0);
        if (c.startBar === next) return false;
        c.startBar = next;
        bus.emit('audio:tracksChanged', audioTracks);
        return true;
    }
    function updateAudioClip(trackId, clipId, patch) {
        const track = audioTracks.find(t => t.id === trackId);
        if (!track) return false;
        const c = track.clips.find(c => c.id === clipId);
        if (!c) return false;
        Object.assign(c, patch);
        bus.emit('audio:tracksChanged', audioTracks);
        return true;
    }
    function getAudioClipsAtBar(bar) {
        const out = [];
        for (const t of audioTracks) {
            for (const c of t.clips) {
                if (Math.round(c.startBar) === bar) out.push({ ...c, trackId: t.id });
            }
        }
        return out;
    }
    // Step-precise variant — required because a left-trimmed clip can land
    // mid-bar and bar-rounding would miss it.
    function getAudioClipsAtStep(step) {
        const out = [];
        for (const t of audioTracks) {
            for (const c of t.clips) {
                const startStep = Math.round(c.startBar * STEPS_PER_BAR);
                if (startStep === step) out.push({ ...c, trackId: t.id });
            }
        }
        return out;
    }
    function clearAudioTracks() {
        if (audioTracks.length === 0) return;
        audioTracks = [];
        bus.emit('audio:tracksChanged', audioTracks);
    }
    function replaceAudioTracks(tracks = []) {
        audioTracks = tracks.map(t => ({
            id: t.id || makeTrackId(),
            name: t.name || 'Audio',
            channelId: t.channelId || t.id,
            clips: (t.clips || []).map(c => ({
                id: c.id || makeAudioClipId(),
                bufferKey: c.bufferKey,
                startBar: Math.max(0, Number(c.startBar) || 0),
                durationSec: c.durationSec,
                name: c.name,
                offsetSec: Number(c.offsetSec) || 0,
                playSec: Number.isFinite(c.playSec) ? c.playSec : c.durationSec,
                fadeInSec: Number(c.fadeInSec) || 0,
                fadeOutSec: Number(c.fadeOutSec) || 0,
            })),
        }));
        bus.emit('audio:tracksChanged', audioTracks);
    }

    // Audio clips also extend the timeline length (for arrangement loop).
    // totalBars() needs to consider the maximum of pattern clips and audio clips.
    function totalBarsIncludingAudio(bpm = 120) {
        let max = totalBars();
        for (const t of audioTracks) {
            for (const c of t.clips) {
                const playSec = Number.isFinite(c.playSec) ? c.playSec : (c.durationSec || 1);
                const bars = playSec ? Math.ceil(playSec * bpm / 240) : 1;
                max = Math.max(max, c.startBar + bars);
            }
        }
        return max;
    }
    // Step-precise total used by the scheduler so fractional pattern clips
    // (e.g. an 8-step pattern occupying half a bar) loop at the correct length.
    function totalStepsIncludingAudio(bpm = 120) {
        let maxSteps = clips.reduce(
            (m, c) => Math.max(m, Math.round((c.startBar + c.lengthBars) * STEPS_PER_BAR)),
            0,
        );
        for (const t of audioTracks) {
            for (const c of t.clips) {
                const bars = c.durationSec ? Math.ceil(c.durationSec * bpm / 240) : 1;
                maxSteps = Math.max(maxSteps, Math.round((c.startBar + bars) * STEPS_PER_BAR));
            }
        }
        return maxSteps;
    }

    return {
        STEPS_PER_BAR,
        setMode, getMode,
        addClip, removeClip, moveClip, resizeClip, changeClipPattern,
        getClips, findClipAt, findClipAtStep, totalBars,
        getCursorBar, setCursorBar,
        clear, replaceAll, pruneRemoved,
        // Audio tracks
        addAudioTrack, removeAudioTrack, renameAudioTrack,
        addAudioClip, removeAudioClip, moveAudioClip, updateAudioClip,
        getAudioTracks, getAudioTrack, getAudioClipsAtBar, getAudioClipsAtStep,
        clearAudioTracks, replaceAudioTracks,
        totalBarsIncludingAudio, totalStepsIncludingAudio,
    };
}
